import { DateTime } from 'luxon'
import { MonthPicker } from 'parkdepot-shared/components/DatePicker/components/MonthPicker/MonthPicker'
import { BookingPricingModelType } from 'parkdepot-shared/gql/graphql'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SubscriptionsWarning from '../../../../components/SubscriptionsWarning/SubscriptionsWarning.component'
import { CheckoutContext } from '../../../../context/CheckoutContext/checkout.context'
import { LotContext } from '../../../../context/LotContext/lot.context'
import { useGetUser } from '../../../../hooks/useAuth'
import { LotPricing } from '../../../../types/userData.interface'
import { getLotPricingMemoized } from '../../../../utils/getLotPricing.utils'
import SubscriptionsContainer, { MonthSelectContainer, SectionTitle } from './Subscriptions.style'
import { LotViewName } from '@customTypes/lot.interface'
import { useHistory } from 'react-router-dom'
import { lotRoute } from '@views/Lot/route'

const Subscriptions = () => {
    const { t } = useTranslation()
    const history = useHistory()

    const { user } = useGetUser()
    const { lot, setNextDisabled } = useContext(LotContext)
    const { modifyAndSetSubscriptionCheckout } = useContext(CheckoutContext)
    const [minimumDate, setMinimumDate] = useState<DateTime>(DateTime.now().plus({ months: 1 }).startOf('month'))
    const [selectedMonth, setSelectedMonth] = useState<DateTime>()

    const onMonthChange = (date: DateTime) => {
        setSelectedMonth(date.startOf('month'))
    }

    useEffect(() => {
        if (lot && lot.earliest_booking_date) {
            const feature = lot.active_features.find(
                (f) => f.name.toLocaleLowerCase() === LotViewName.SUBSCRIPTION.toLowerCase()
            )
            if (!feature) {
                history.push(lotRoute(lot.id))
            }
            const earliestBookingDate = DateTime.fromISO(lot.earliest_booking_date)
            // if earliest booking date is in the past, set the minimum date to the next month
            if (earliestBookingDate < DateTime.now()) {
                setMinimumDate(DateTime.now().plus({ months: 1 }).startOf('month'))
                setSelectedMonth(DateTime.now().plus({ months: 1 }).startOf('month'))
            } else {
                // if day is 1, don't add a month
                if (earliestBookingDate.day === 1) {
                    setMinimumDate(earliestBookingDate.startOf('month'))
                    setSelectedMonth(earliestBookingDate.startOf('month'))
                } else {
                    setMinimumDate(earliestBookingDate.startOf('month').plus({ months: 1 }))
                    setSelectedMonth(earliestBookingDate.startOf('month').plus({ months: 1 }))
                }
            }
        }
    }, [lot])

    useEffect(() => {
        if (!selectedMonth || !lot) return
        const firstDay = selectedMonth.startOf('month')

        const privatePricingLot: LotPricing = getLotPricingMemoized(user, lot?.id)
        const isDiscountApplied = privatePricingLot.pricing_type !== BookingPricingModelType.Public
        const currentPricingType = isDiscountApplied ? privatePricingLot.pricing_type : BookingPricingModelType.Public

        const monthlyPrice = lot?.prices?.find((p) => p.pricing_type === currentPricingType)?.month
        if (monthlyPrice) {
            modifyAndSetSubscriptionCheckout(lot, monthlyPrice, firstDay)
            // temp hack
            setTimeout(() => {
                setNextDisabled(false)
            }, 300)
        }
    }, [selectedMonth, user, lot, minimumDate, modifyAndSetSubscriptionCheckout, setNextDisabled])

    return (
        <SubscriptionsContainer>
            <SectionTitle>{t('lot.subscriptionsTitle')}</SectionTitle>
            <MonthSelectContainer className="month-select-container">
                <MonthPicker minDate={minimumDate} value={selectedMonth} onChange={onMonthChange} />
            </MonthSelectContainer>
            <SubscriptionsWarning lotId={lot?.id ?? ''} />
        </SubscriptionsContainer>
    )
}

export default Subscriptions
